/**
 * **Choices** — one field on the visit editor (`docs/quote-build-refresh.md`
 * §8, boards qb-05 and qb-10 · 5–6).
 *
 * It replaces two controls that did one job: a free-text box naming the
 * question and a separate list of plans. One list — the choices on this quote,
 * never the catalog — ticked as many as apply, with find-or-create in the same
 * box and the question the customer is asked read back underneath.
 *
 * Props only, so step 8 moves it to `builder/fields/choices-field.tsx`
 * unchanged. The editor owns the ticks until Done; creating a choice writes it
 * at once (§8.4) so a second visit's field can already list it.
 */
import { useState, type MouseEvent, type ReactNode } from 'react'
import { usePhone } from '../../lib/use-narrow.js'
import { MenuMultiSelect } from '../../components/menu-select.js'
import { Button } from '../../components/primitives.js'
import { EditorInput } from '../editor-shell.js'
import { IconEdit, IconTag } from '../../frame/icons.js'
import {
  ALREADY_TICKED, CHOICES_ADD, CHOICES_CREATE, CHOICES_CREATE_IN, CHOICES_CREATE_WORD, CHOICES_EMPTY, CHOICES_FIND,
  CHOICES_FIRST, CHOICES_HINT, CHOICES_LABEL, CHOICES_NEW, CHOICES_NEW_QUESTION, CHOICES_NONE,
  CHOICES_OPTIONAL_REASON, CHOICES_VISITS, CUSTOMER_IS_ASKED, DONE, EVERY_PRICE, IN_BOX_NO_CHOICE, KEEP_IT_IN,
  MOVE_IT, REWORD, REWORD_HINT, SAVE, inChoicesText,
} from '../copy.js'

/** The heading the ChipsMultiSelect hands back for "+ New question". */
export const NEW_QUESTION = '__new__'

export interface ChoicesFieldProps {
  visitName: string
  /** How many trips the visit is, for "five visits appear in …"; null when it has no count. */
  trips: number | null
  groups: { id: string; name: string }[]
  options: { id: string; groupId: string; name: string; visitIds: string[]; declined: boolean }[]
  /** The ticked choices — the whole set, never a delta. */
  value: string[]
  onChange: (ids: string[]) => void
  /** Writes the choice now and answers with it, so it can be ticked. */
  onCreate: (name: string, groupId?: string, newQuestion?: boolean)
    => Promise<{ id: string; groupId: string } | null>
  onRename: (groupId: string, name: string) => Promise<boolean>
  /** Optional is ticked: the field goes quiet and says why (qb-05 · 6). */
  optional: boolean
  /** The visit was in a choice when the editor opened. */
  wasInChoice: boolean
  /**
   * The choice box the visit sits in, when it is in one (step 22 item 2.2).
   * With nothing ticked it stays there — in no choice, off the proposal —
   * and the line under the field says so rather than "in every price".
   */
  boxName?: string | null
  readOnly?: boolean
  /**
   * Told when the list opens and closes. The list now sits in the editor's
   * flow, and the editor's `Popover` re-measures only when it re-renders —
   * so a caller in one passes a state setter here and the popover grows by
   * the list's height (qb-05: 420 closed, 590 open) instead of scrolling.
   */
  onListOpenChange?: (open: boolean) => void
}

export function ChoicesField(props: ChoicesFieldProps) {
  const { groups, options, value, onChange, optional, readOnly } = props
  const phone = usePhone()
  const live = options.filter((option) => !option.declined)
  const nameOf = (groupId: string) => groups.find((group) => group.id === groupId)?.name ?? ''
  const groupOfOption = (id: string) => live.find((option) => option.id === id)?.groupId
  const tickedGroup = value.map(groupOfOption).find(Boolean)

  /** The Move panel: which choice asked, from which question to which (qb-05 · 5). */
  const [pending, setPending] = useState<{
    from: string; to: string; resolve: (ok: boolean) => void
  } | null>(null)
  const [rewording, setRewording] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  /*
   * qb-05 · 2–3: the open list is part of the editor's flow — it pushes the
   * footer down rather than covering it — and while it is open nothing is
   * drawn under it; the hint and the read-back come back when it closes.
   */
  const [listOpen, setListOpen] = useState(false)
  const under = !(listOpen && !phone)

  const ask = (to: string): Promise<boolean> => {
    const from = tickedGroup
    if (!from || from === to) return Promise.resolve(true)
    return new Promise((resolve) => setPending({ from, to, resolve }))
  }
  const answer = (ok: boolean) => { pending?.resolve(ok); setPending(null) }

  const create = async (name: string, groupId?: string) => {
    const made = await props.onCreate(
      name,
      groupId && groupId !== NEW_QUESTION ? groupId : undefined,
      groupId === NEW_QUESTION)
    if (!made) return
    if (tickedGroup && made.groupId !== tickedGroup) {
      if (!await ask(made.groupId)) return
      onChange([made.id])
      return
    }
    onChange([...value, made.id])
  }

  const headed = groups.filter((group) => live.some((option) => option.groupId === group.id))
  const disabled = optional || readOnly
  const ticked = live.filter((option) => value.includes(option.id))

  const prompt: ReactNode = pending && (
    <div className="border-t border-warn/30 bg-warn-bg px-3 pb-3 pt-2.5 text-[12px] leading-[1.4] text-warn">
      This visit is in <b>{nameOf(pending.from)}</b>. A visit can be in one question at a time — move it to{' '}
      <b>{nameOf(pending.to)}</b>?
      <div className="mt-2 flex gap-2">
        <Button variant="primary" className="!h-7 !px-3 !text-[12px]" onClick={() => answer(true)}>
          {MOVE_IT}
        </Button>
        <Button className="!h-7 !px-3 !text-[12px]" onClick={() => answer(false)}>
          {KEEP_IT_IN(nameOf(pending.from))}
        </Button>
      </div>
    </div>
  )

  return (
    <div data-choices-field="" className="flex flex-col">
      <span className="mb-1.5 text-fieldlabel text-text-muted">{CHOICES_LABEL}</span>
      <MenuMultiSelect
        chips
        label={CHOICES_LABEL}
        placeholder={CHOICES_NONE}
        searchPlaceholder={CHOICES_FIND}
        disabled={disabled}
        values={disabled && optional ? [] : value}
        onChange={onChange}
        options={live.map((option) => ({
          value: option.id,
          label: option.name,
          group: option.groupId,
          detail: CHOICES_VISITS(option.visitIds.length),
        }))}
        groups={headed.length >= 2 ? headed.map((group) => ({
          id: group.id, label: group.name, addLabel: CHOICES_ADD,
        })) : []}
        onCreate={(name, groupId) => void create(name, groupId)}
        createLabel={(name, groupId) => groupId && groupId !== NEW_QUESTION && headed.length >= 2
          ? CHOICES_CREATE_IN(name, nameOf(groupId))
          : <span aria-label={CHOICES_CREATE(name)}>{CHOICES_CREATE_WORD} <b>“{name}”</b></span>}
        createTag={live.length === 0 ? CHOICES_FIRST : undefined}
        emptyText={CHOICES_EMPTY}
        footLabel={headed.length >= 2 ? CHOICES_NEW_QUESTION : CHOICES_NEW}
        confirmToggle={(_value, groupId) => ask(groupId)}
        prompt={prompt}
        sheet={{ title: CHOICES_LABEL, sub: props.visitName, doneLabel: DONE, alreadyLabel: ALREADY_TICKED }}
        inline
        onOpenChange={(open) => { setListOpen(open); props.onListOpenChange?.(open) }}
      />

      {under && optional && (
        <span className="mt-1.5 text-[11.5px] leading-[1.4] text-text-faint">{CHOICES_OPTIONAL_REASON}</span>
      )}
      {under && !optional && ticked.length === 0 && (
        <span className="mt-1.5 text-[11.5px] leading-[1.4] text-text-faint">
          {props.boxName ? IN_BOX_NO_CHOICE(props.boxName) : props.wasInChoice ? EVERY_PRICE : CHOICES_HINT}
        </span>
      )}

      {under && !optional && ticked.length > 0 && tickedGroup && (
        rewording === tickedGroup
          ? (
            <div className="mt-2 flex flex-col">
              <span className="mb-2 flex items-center gap-1.5 text-[12px] text-text-muted">
                <span style={{ color: 'rgb(var(--status-quoteLine-deployment-fg))' }}><IconTag size={13} /></span>
                {CUSTOMER_IS_ASKED}
              </span>
              <div className="flex items-center gap-2">
                <EditorInput
                  autoFocus
                  value={draft}
                  aria-label="The question"
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      void props.onRename(tickedGroup, draft.trim()).then((ok) => { if (ok) setRewording(null) })
                    }
                    if (event.key === 'Escape') { event.stopPropagation(); setRewording(null) }
                  }}
                />
                <Button
                  variant="primary"
                  disabled={!draft.trim()}
                  onClick={() => void props.onRename(tickedGroup, draft.trim())
                    .then((ok) => { if (ok) setRewording(null) })}
                >
                  {SAVE}
                </Button>
              </div>
              <span className="mt-1.5 text-[11.5px] leading-[1.4] text-text-faint">
                {REWORD_HINT(live.filter((option) => option.groupId === tickedGroup)
                  .map((option) => option.name).join(' · '))}
              </span>
            </div>
          )
          : (
            <>
              <span className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px] text-text-muted">
                <span style={{ color: 'rgb(var(--status-quoteLine-deployment-fg))' }}><IconTag size={13} /></span>
                {CUSTOMER_IS_ASKED} <b className="text-text">{nameOf(tickedGroup)}</b>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => { setDraft(nameOf(tickedGroup)); setRewording(tickedGroup) }}
                    className="ml-1 inline-flex items-center gap-1 font-semibold text-accent"
                  >
                    <IconEdit size={12} /> {REWORD}
                  </button>
                )}
              </span>
              <span className="mt-1.5 text-[11.5px] leading-[1.4] text-text-faint">
                {inChoicesText(ticked.map((option) => option.name), props.trips, !phone)}
              </span>
            </>
          )
      )}
    </div>
  )
}

/**
 * A purple chip per choice a visit is in (§9.1, qb-06): up to three, then the
 * first two and "+N" with the rest in the title. A chip is a button onto the
 * visit's Choices field.
 */
export function ChoiceChips({ names, onOpen }: {
  names: string[]
  onOpen?: (event: MouseEvent<HTMLElement>) => void
}) {
  if (names.length === 0) return null
  const shown = names.length > 3 ? names.slice(0, 2) : names
  const more = names.length - shown.length
  const chip = (text: string, key: string, title?: string) => (
    <button
      key={key}
      type="button"
      title={title}
      data-choice-chip=""
      onClick={(event) => { event.stopPropagation(); onOpen?.(event) }}
      style={{
        background: 'rgb(var(--status-quoteLine-deployment-bg))',
        color: 'rgb(var(--status-quoteLine-deployment-fg))',
      }}
      className="inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-[6px] px-[7px] text-[11px] font-semibold"
    >
      {key !== 'more' && <IconTag size={11} />}
      {text}
    </button>
  )
  return (
    <>
      {shown.map((name) => chip(name, name))}
      {more > 0 && chip(`+${more}`, 'more', names.join(' · '))}
    </>
  )
}
